// Add / edit ONE client narrative standard. The edit is STAGED for Billing Team approval
// (billingChangeApi.submitChange, section 'narrative') — nothing is written live.
//
// Mount it only while it should be visible (`<NarrativeEditModal initial={row|null} … />`);
// form state is seeded from `initial` on mount. It renders as a sibling AFTER the manager modal
// inside an `.nsx-stack` wrapper (raised z-index) so it stacks above it; Esc closes only the
// topmost layer (narrativeLayers.js).
//
// Rules mirrored from the server (narrativeValidation.validateDraft) give instant feedback; the
// server re-validates and may still reject with a code (NARRATIVE_CONFLICT, LOCKED_BY_GROUP,
// NARRATIVE_FOREIGN, SIGNED_OFF, …) — its message is toasted.

import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../components/Modal';
import { submitChange } from '../billingChangeApi';
import { NarrativeRichEditor, sanitizeNarrativeHtml } from './NarrativeRichEditor';
import JobMultiSelect from './JobMultiSelect';
import NarrativeStandardsHelp from './NarrativeStandardsHelp';
import { computeOverwrites } from './narrativeOverwrites';
import { typesConflict, validateDraft, effectiveRows } from './narrativeValidation';
import { useEscLayer } from './narrativeLayers';
import './narratives.css';

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label));

// Submitting is OPTIMISTIC: the manager modal shows the standard (with its "Pending approval" tag) via
// `onOptimistic(draft, initial)` — which returns an undo — and this form closes at once. If the server
// refuses (a conflict, a lock, a sign-off…) the list is put back, the message is toasted and the form
// reopens through `onRefused(draft)` with everything still filled in (`draft` seeds it).
export default function NarrativeEditModal({ view, period, client, initial, draft, onClose, onOptimistic, onRefused, onSubmitted }) {
  const isEdit = !!initial;
  const seed = draft || initial;
  const [form, setForm] = useState(() => ({
    uuid: seed?.uuid || crypto.randomUUID().toLowerCase(),
    Level: seed?.Level || 'JOB',
    Serv: seed?.Serv || '',
    Type: seed?.Type || '',
    Idx: seed?.Level === 'JOB' ? (seed.Idx || []).map(Number) : [],
    Narrative: sanitizeNarrativeHtml(seed?.Narrative || ''),
    ApplyToGroup: !!seed?.ApplyToGroup,
  }));
  const [ovwOpen, setOvwOpen] = useState(false);
  const formatRef = useRef(null);

  useEscLayer(true, () => onClose());

  const set = patch => setForm(f => ({ ...f, ...patch }));

  // Service picklist: the client's engaged services (label = title when present); a row being
  // edited may keep a service the client no longer has.
  const serviceOptions = useMemo(() => {
    const opts = (view.services || []).map(s => ({ value: String(s.key), label: String(s.meta || s.label || s.key) }));
    if (initial && initial.Serv && !opts.some(o => o.value === String(initial.Serv))) {
      opts.push({ value: String(initial.Serv), label: `${initial.Serv} (no longer engaged)` });
    }
    // 'ALL' (every service) is always pinned first.
    return opts.sort((a, b) => (b.value === 'ALL') - (a.value === 'ALL') || byLabel(a, b));
  }, [view.services, initial]);

  // Job picklist: the client's active jobs (plus any job the edited row already carries).
  const allJobOptions = useMemo(() => {
    const opts = (view.jobs || []).map(j => ({ value: Number(j.key), label: String(j.label ?? j.key) }));
    for (const i of (initial?.Idx || [])) {
      if (!opts.some(o => o.value === Number(i))) opts.push({ value: Number(i), label: `Job #${i} (no longer active)` });
    }
    return opts.sort(byLabel);
  }, [view.jobs, initial]);
  const jobLabel = i => (allJobOptions.find(o => o.value === Number(i))?.label) || `#${i}`;

  const eff = useMemo(() => effectiveRows(view.rows), [view.rows]);

  // Jobs already carried by ANOTHER standard with a conflicting type for the same service are
  // hidden (unless selected here). Undecidable until Type + Service are chosen.
  const unavailable = useMemo(() => {
    const s = new Set();
    if (!form.Type || !form.Serv) return s;
    for (const n of eff) {
      if (n.uuid === form.uuid || n.Level !== 'JOB') continue;
      if (!typesConflict(n.Type, form.Type) || String(n.Serv) !== String(form.Serv)) continue;
      for (const i of (n.Idx || [])) s.add(String(i));
    }
    return s;
  }, [eff, form.uuid, form.Type, form.Serv]);

  const jobOptions = useMemo(
    () => allJobOptions.filter(o => form.Idx.some(i => String(i) === String(o.value)) || !unavailable.has(String(o.value))),
    [allJobOptions, form.Idx, unavailable],
  );

  // "Overwriting …" badge — which lower-precedence standards this entry would take over.
  const overwrite = useMemo(
    () => computeOverwrites((view.context || []).filter(r => r.uuid !== form.uuid), {
      scopeKey: view.clientCode, parentKey: view.parentCode,
      level: form.Level, type: form.Type, idx: form.Idx, serv: form.Serv,
      staffEmails: view.staffEmails,
    }),
    [view.context, view.clientCode, view.parentCode, view.staffEmails, form.uuid, form.Level, form.Type, form.Idx, form.Serv],
  );
  const showOvw = overwrite.specificTotal > 0 || !!overwrite.blanket;

  function onServChange(serv) {
    // Job availability is per-service, so a different service clears the job selection.
    set(serv === form.Serv ? { Serv: serv } : { Serv: serv, Idx: [] });
  }
  function onLevelChange(level) {
    set(level === 'JOB' ? { Level: level } : { Level: level, Idx: [] });
  }

  function submit() {
    const draft = {
      uuid: form.uuid,
      Level: form.Level,
      Idx: form.Level === 'JOB' ? form.Idx.map(Number) : [],
      Type: form.Type,
      Serv: form.Serv,
      Narrative: sanitizeNarrativeHtml(form.Narrative),
      ApplyToGroup: !!view.isParent && !!form.ApplyToGroup,
    };
    const problem = validateDraft(draft, {
      rows: view.rows, services: view.services, jobs: view.jobs, prev: initial,
      isParent: view.isParent, parentCode: view.parentCode, jobLabel,
    });
    if (problem) { toast.error(problem); return; }

    const undo = onOptimistic?.(draft, initial);
    onClose();
    submitChange({
      period, contIndex: client.contIndex, section: 'narrative', op: 'set',
      payload: {
        uuid: draft.uuid, Level: draft.Level, Idx: draft.Idx, Type: draft.Type,
        Serv: draft.Serv, Narrative: draft.Narrative, ApplyToGroup: draft.ApplyToGroup,
      },
    }).then(
      () => { toast.success('Submitted for Billing Team approval.'); onSubmitted?.(); },
      err => {
        undo?.();
        toast.error(err.message, { autoClose: 10000 });
        onRefused?.(draft);
      },
    );
  }

  const fmtBtn = (cmd, title, label) => (
    <button
      type="button" title={title} className="nsx-fmt-btn"
      onMouseDown={e => e.preventDefault()}           // keep the editor's selection
      onClick={() => formatRef.current?.(cmd)}
    >{label}</button>
  );

  return (
    <div className="nsx-stack">
      <Modal
        open
        onClose={onClose}
        title={isEdit ? 'Edit Narrative Standard' : 'Add Narrative Standard'}
        footer={(
          <>
            <button type="button" className="cs-btn" onClick={onClose}>Cancel</button>
            <button type="button" className="cs-btn cs-btn--save" onClick={submit}>
              Submit for approval
            </button>
          </>
        )}
      >
        <div className="nsx-form">
          <div className="nsx-form-top">
            <span className="nsx-form-note">
              <i className="fas fa-hourglass-half" /> Saved as a request — the Billing Team approves it before it takes effect.
            </span>
            <NarrativeStandardsHelp small />
          </div>

          {initial?._pending && (
            <div className="nsx-form-warn">
              <i className="fas fa-info-circle" /> This standard already has a pending request. Submitting replaces it.
            </div>
          )}

          <div className="nsx-form-grid">
            <label className="nsx-field">
              <span>Level</span>
              <select className="nsx-select" value={form.Level} onChange={e => onLevelChange(e.target.value)}>
                <option value="JOB">JOB</option>
                <option value="SERV">SERV</option>
              </select>
            </label>
            <label className="nsx-field">
              <span>Type</span>
              <select className="nsx-select" value={form.Type} onChange={e => set({ Type: e.target.value })}>
                <option value="" disabled>— select type —</option>
                <option value="ALL">ALL</option>
                <option value="DISB">DISB</option>
                <option value="TIME">TIME</option>
              </select>
            </label>
            <label className="nsx-field nsx-field--full">
              <span>Service</span>
              <select className="nsx-select" value={form.Serv} onChange={e => onServChange(e.target.value)}>
                <option value="" disabled>— select service —</option>
                {serviceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>

          {form.Level === 'JOB' && (
            <div className="nsx-field">
              <span>Job(s)</span>
              {form.Serv === '' ? (
                <div className="nsx-hint">Select a service first.</div>
              ) : (
                <JobMultiSelect
                  options={jobOptions}
                  selected={form.Idx}
                  onChange={idx => set({ Idx: idx })}
                />
              )}
              {form.Serv !== '' && form.Type === '' && (
                <div className="nsx-hint">Pick a Type to hide jobs already covered by another standard.</div>
              )}
            </div>
          )}

          {view.isParent && (
            <label className="nsx-check" title="Applies to every child in the billing group and locks this job/service for them">
              <input
                type="checkbox"
                checked={!!form.ApplyToGroup}
                onChange={e => set({ ApplyToGroup: e.target.checked })}
              />
              <span>Apply to all billing group <em>(locks this job/service for every child)</em></span>
            </label>
          )}

          <div className="nsx-field">
            <div className="nsx-narr-head">
              <span>Narrative</span>
              {showOvw && (
                <button type="button" className="nsx-ovw-badge" onClick={() => setOvwOpen(o => !o)} aria-expanded={ovwOpen}>
                  <i className="fas fa-layer-group" />{' '}
                  {overwrite.specificTotal > 0
                    ? `Overwriting ${overwrite.specificTotal} Standard${overwrite.specificTotal === 1 ? '' : 's'}`
                    : 'Overwriting Standards'}
                  <i className={`fas fa-chevron-${ovwOpen ? 'up' : 'down'} nsx-ovw-chev`} />
                </button>
              )}
            </div>
            {showOvw && ovwOpen && (
              <div className="nsx-ovw-pop">
                <div className="nsx-ovw-pop-title">Takes precedence over:</div>
                {overwrite.specific.length > 0 && (
                  <ul className="nsx-ovw-list">
                    {overwrite.specific.map((s, i) => (
                      <li key={i}>
                        <span className="nsx-ovw-tier">{s.tierLabel}{s.owner ? ` (${s.owner})` : ''} · {s.type}</span>
                        <div className="nsx-ovw-text">{s.narrative || '(no narrative text)'}</div>
                      </li>
                    ))}
                  </ul>
                )}
                {overwrite.blanket && <div className="nsx-ovw-note">{overwrite.blanket.message}</div>}
              </div>
            )}
            <div className="nsx-rte-toolbar" role="toolbar" aria-label="Narrative formatting">
              {fmtBtn('bold', 'Bold (Ctrl+B)', <b>B</b>)}
              {fmtBtn('italic', 'Italic (Ctrl+I)', <i>I</i>)}
              {fmtBtn('underline', 'Underline (Ctrl+U)', <u>U</u>)}
            </div>
            <NarrativeRichEditor
              className="nsx-rte"
              value={form.Narrative}
              onChange={html => set({ Narrative: html })}
              onActivate={fmt => { formatRef.current = fmt; }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
